/**
 * Clip 生成 page (Phase 7 PR2、Figma 05 node-id=15-2)。
 *
 * Layout:
 * - 左カラム: 原動画プレビュー (YouTube embed) + 区間調整 (start/end input)
 * - 右カラム: 候補情報 + 9:16 変換 (crop) + 音声・出力 (normalize/file/bg_overlay) + 生成ボタン
 *
 * SSE で `clip.queued/running/success/failed` を watch して status を反映。
 * 成功時は `download_url` が presigned URL なので、ダウンロードボタンで開く。
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import type { ClipResponse, ClipStatus, RunEvent } from "../api/types"

// UI で選択可能な crop モード (Figma 05 確定: center / fit の 2 択。
// `none` は CLI / API のみ、UI からは到達不可。Copilot review 2nd #8 反映)。
type UiCropMode = "center" | "fit"

import { ApiError, apiFetch } from "../lib/api"
import { useRunEvents } from "../lib/sse"

interface Candidate {
  rank: number
  start_sec: number
  end_sec: number
  primary_score?: number
  reason?: string
}

interface RunSource {
  video_id?: string
  url?: string
  title?: string
  channel?: string
}

// HH:MM:SS.D の human-readable と秒の相互変換。0.0 秒精度 (start/end は YouTube embed の
// `start` パラメタが int 秒精度のため、0.0 秒以下は ffmpeg 側で意味を持ちつつ、UI 上は
// "20:34.5" の表示で十分とする。
function formatTimecode(sec: number): string {
  if (Number.isNaN(sec) || sec < 0) return "0:00.0"
  const m = Math.floor(sec / 60)
  const s = sec - m * 60
  return `${m}:${s.toFixed(1).padStart(4, "0")}`
}

function parseTimecode(str: string): number | null {
  // strict regex 形式 (codex review 反映、旧実装は `parseFloat` で "1:02abc" 等が通る bug):
  // - "HH:MM:SS(.fff)" (時:分:秒): 例 1:20:34.5 → 4834.5
  // - "MM:SS(.fff)" (分:秒): 例 20:34.5 → 1234.5
  // - "SS(.fff)" (秒のみ): 例 34.5 → 34.5
  // 各 component は 0-9 数字のみ、秒の小数点以下は **任意桁** 許可 (`\d+`)。UI は .1 桁
  // 精度想定だが、入力で 34.567 等が来ても float に通す (ffmpeg seek 精度は ms オーダー)。
  const t = str.trim()
  if (!t) return null
  const hhmmss = /^(\d+):([0-5]?\d):([0-5]?\d(?:\.\d+)?)$/.exec(t)
  if (hhmmss) {
    return Number(hhmmss[1]) * 3600 + Number(hhmmss[2]) * 60 + Number(hhmmss[3])
  }
  const mmss = /^(\d+):([0-5]?\d(?:\.\d+)?)$/.exec(t)
  if (mmss) {
    return Number(mmss[1]) * 60 + Number(mmss[2])
  }
  const ss = /^(\d+(?:\.\d+)?)$/.exec(t)
  if (ss) {
    return Number(ss[1])
  }
  return null
}

interface ResultJsonShape {
  source?: RunSource
  candidates?: Candidate[]
}

export function ClipGen() {
  const { runId, rank: rankStr } = useParams<{ runId: string; rank: string }>()
  const rank = Number.parseInt(rankStr ?? "0", 10)

  // 1. result.json 取得 (candidate / source 情報)
  const [result, setResult] = useState<ResultJsonShape | null>(null)
  const [resultError, setResultError] = useState<string | null>(null)
  useEffect(() => {
    if (!runId) return
    apiFetch<ResultJsonShape>(`/api/runs/${encodeURIComponent(runId)}`)
      .then(setResult)
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e)
        setResultError(msg)
      })
  }, [runId])

  const candidate = useMemo<Candidate | null>(() => {
    if (!result?.candidates) return null
    return result.candidates.find((c) => c.rank === rank) ?? null
  }, [result, rank])

  // 2. UI state (toggle / inputs)。UI からは center / fit のみ選択可能、
  // none は CLI / API 経路でのみ (Figma 05 確定)。
  const [crop, setCrop] = useState<UiCropMode>("center")
  const [normalize, setNormalize] = useState<boolean>(true)
  const [bgOverlay, setBgOverlay] = useState<boolean>(false)
  const [startInput, setStartInput] = useState<string>("")
  const [endInput, setEndInput] = useState<string>("")
  const [inputError, setInputError] = useState<string | null>(null)

  // candidate ロード時に start/end input を初期化
  useEffect(() => {
    if (candidate) {
      setStartInput(formatTimecode(candidate.start_sec))
      setEndInput(formatTimecode(candidate.end_sec))
    }
  }, [candidate])

  // 3. 既存 clip status の取得 + SSE で更新を反映
  const [clipState, setClipState] = useState<ClipResponse | null>(null)
  const refreshClipStatus = useCallback(async () => {
    if (!runId) return
    try {
      const r = await apiFetch<ClipResponse>(`/api/runs/${encodeURIComponent(runId)}/clips/${rank}`)
      setClipState(r)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // 未生成 = 404、null のまま (生成ボタンを押せる状態)
        setClipState(null)
      }
    }
  }, [runId, rank])
  useEffect(() => {
    refreshClipStatus()
  }, [refreshClipStatus])

  // 4. SSE: clip.* event を watch
  const sse = useRunEvents(runId)
  useEffect(() => {
    if (!runId) return
    // 最新の clip.* event を逆順 scan して、自 rank の status を反映
    for (let i = sse.events.length - 1; i >= 0; i--) {
      const ev: RunEvent = sse.events[i]
      if (!ev.event_type.startsWith("clip.")) continue
      const payload = ev.payload as Record<string, unknown>
      const evRank = typeof payload.rank === "number" ? payload.rank : null
      if (evRank !== rank) continue
      // status を event_type から派生
      const status: ClipStatus =
        ev.event_type === "clip.queued"
          ? "queued"
          : ev.event_type === "clip.running"
            ? "running"
            : ev.event_type === "clip.success"
              ? "success"
              : "failed"
      // success / failed のときは GET で詳細を取り直す (download_url を埋めるため)。
      // queued / running のときも state を更新する。Copilot review 2nd #7 反映で、
      // prev=null (= GET 404 直後 / 別タブで enqueue され既存 state が無い) の場合は
      // refreshClipStatus を呼んで record を取り直す (UI が「未生成」のまま固まる bug 修正)。
      // **副作用は state updater の外で実施** (Copilot 4th #6、updater 内で fetch を起こすと
      // StrictMode の double-invoke で重複 fetch / state ループの原因)。
      if (status === "success" || status === "failed") {
        refreshClipStatus()
      } else if (clipState) {
        setClipState({ ...clipState, status })
      } else {
        // prev=null = まだ GET で取れていない or 別タブで enqueue 済。
        refreshClipStatus()
      }
      break
    }
  }, [sse.events, rank, refreshClipStatus, runId, clipState])

  // 5. 生成ボタン
  const [submitting, setSubmitting] = useState(false)
  const handleSubmit = useCallback(async () => {
    if (!runId || !candidate) return
    setInputError(null)
    const startSec = parseTimecode(startInput)
    const endSec = parseTimecode(endInput)
    if (startSec === null || endSec === null) {
      setInputError("開始/終了時刻のフォーマットが不正です (例: 20:34.5)")
      return
    }
    if (endSec <= startSec) {
      setInputError("終了時刻は開始時刻より後である必要があります")
      return
    }
    // UI 上は crop=none を選択不能 (UiCropMode で center/fit に絞っている、Copilot
    // review 2nd #8 反映)。crop=none + bg_overlay 組合せは CLI / API 経路でのみ発生し、
    // その場合 backend が 422 で reject するので UI 側の追加 check は不要。
    setSubmitting(true)
    try {
      const resp = await apiFetch<ClipResponse>(`/api/runs/${encodeURIComponent(runId)}/clips`, {
        method: "POST",
        body: {
          rank,
          crop,
          normalize: normalize ? "on" : "off",
          bg_overlay: bgOverlay ? "on" : "off",
          start_sec: startSec,
          end_sec: endSec,
        },
      })
      setClipState(resp)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setInputError(`生成リクエスト失敗: ${msg}`)
    } finally {
      setSubmitting(false)
    }
  }, [runId, candidate, startInput, endInput, crop, bgOverlay, normalize, rank])

  // 6. ダウンロード URL を取得 (success 時)
  const handleDownload = useCallback(async () => {
    if (!runId || !clipState || clipState.status !== "success") return
    // codex review 反映: GET /clips/{rank} は status JSON、ダウンロードは
    // /download 派生 endpoint (S3 presigned URL へ 302 redirect)。
    // window.open で新タブを開くと browser がリンクを踏む = redirect も browser が辿る。
    window.open(`/api/runs/${encodeURIComponent(runId)}/clips/${rank}/download`, "_blank")
  }, [runId, clipState, rank])

  // ---------- render ----------

  if (resultError) {
    return (
      <div className="p-8 text-omc-text-primary">
        <p className="text-omc-semantic-danger">読み込みエラー: {resultError}</p>
        <Link to={runId ? `/runs/${runId}` : "/runs"} className="mt-4 text-omc-accent underline">
          ← run 詳細へ戻る
        </Link>
      </div>
    )
  }

  if (!result) {
    return <div className="p-8 text-omc-text-secondary">読み込み中…</div>
  }
  if (!candidate) {
    // result は取得できたが rank が範囲外。Copilot review 3rd #8 反映で「読み込み中」固定を回避。
    return (
      <div className="p-8 text-omc-text-primary">
        <p className="text-omc-semantic-danger">
          候補 #{rank} は見つかりませんでした (rank が範囲外、または candidate が削除済)。
        </p>
        <Link
          to={runId ? `/runs/${runId}` : "/runs"}
          className="mt-4 inline-block text-omc-accent underline"
        >
          ← run 詳細へ戻る
        </Link>
      </div>
    )
  }

  const videoId = result.source?.video_id ?? null
  const duration = candidate.end_sec - candidate.start_sec
  const statusBadge = clipState ? statusToLabel(clipState.status) : "未生成"
  // Copilot review #7 反映: queued 中も backend が 409 を返すので UI 側で送信不可に。
  const canSubmit =
    !submitting && (!clipState || (clipState.status !== "running" && clipState.status !== "queued"))

  return (
    <div className="min-h-screen bg-omc-bg p-8 text-omc-text-primary">
      {/* breadcrumb */}
      <nav className="text-sm text-omc-text-secondary">
        <Link to="/runs" className="hover:underline">
          Runs
        </Link>
        <span className="mx-2">›</span>
        <Link to={`/runs/${runId}`} className="hover:underline">
          {runId}
        </Link>
        <span className="mx-2">›</span>
        <span>候補 #{rank} — クリップ生成</span>
      </nav>

      <h1 className="mt-6 text-3xl font-bold">クリップ生成</h1>
      <p className="mt-2 text-omc-text-secondary">
        候補から 9:16 縦型 mp4 を生成。レビュー用 / 外部編集ツール (DaVinci / Premiere / CapCut 等)
        への素材として利用してください。最終加工は外部編集ツールに委ねます (SPEC §4.8.5)。
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[480px_1fr]">
        {/* 左カラム: プレビュー + 区間調整 */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-omc-text-secondary">プレビュー</h2>
          <div className="aspect-video bg-omc-surface flex items-center justify-center rounded-lg border border-omc-border">
            {videoId ? (
              <iframe
                title="YouTube preview"
                width="100%"
                height="100%"
                // Copilot 4th #5 反映: 入力が parse できればそれを優先、できなければ
                // candidate 既定値にフォールバック (区間調整しながらプレビューを更新)。
                src={(() => {
                  const parsedStart = parseTimecode(startInput)
                  const parsedEnd = parseTimecode(endInput)
                  const sStart = parsedStart !== null ? parsedStart : candidate.start_sec
                  const sEnd = parsedEnd !== null ? parsedEnd : candidate.end_sec
                  return `https://www.youtube.com/embed/${videoId}?start=${Math.floor(sStart)}&end=${Math.ceil(sEnd)}`
                })()}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              />
            ) : (
              <span className="text-omc-text-secondary">原動画プレビュー (YouTube)</span>
            )}
          </div>

          <div className="mt-2">
            <h2 className="text-sm font-semibold text-omc-text-secondary">区間調整</h2>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs text-omc-text-secondary">開始</span>
                <input
                  type="text"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                  className="mt-1 w-full rounded border border-omc-border bg-omc-surface p-2 text-omc-text-primary"
                  placeholder="20:34.5"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-omc-text-secondary">終了</span>
                <input
                  type="text"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                  className="mt-1 w-full rounded border border-omc-border bg-omc-surface p-2 text-omc-text-primary"
                  placeholder="21:29.0"
                />
              </label>
            </div>
          </div>
        </div>

        {/* 右カラム: 設定 + 生成ボタン */}
        <div className="space-y-6">
          {/* 候補情報 */}
          <section className="rounded-lg border border-omc-border bg-omc-surface p-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">候補 #{rank}</h2>
              {candidate.primary_score !== undefined && (
                <span className="rounded bg-omc-accent/20 px-2 py-1 text-sm text-omc-accent">
                  {candidate.primary_score.toFixed(2)}
                </span>
              )}
              <span className="text-sm text-omc-text-secondary">
                {formatTimecode(candidate.start_sec)} → {formatTimecode(candidate.end_sec)} ·{" "}
                {duration.toFixed(1)}s
              </span>
            </div>
            {candidate.reason && (
              <p className="mt-2 text-sm text-omc-text-secondary">{candidate.reason}</p>
            )}
          </section>

          {/* 9:16 変換 */}
          <section className="rounded-lg border border-omc-border bg-omc-surface p-4">
            <h2 className="text-lg font-semibold">9:16 変換</h2>
            <div className="mt-3">
              <span className="block text-sm text-omc-text-secondary">クロップ方式</span>
              <div className="mt-2 flex gap-2">
                <CropChip
                  label="センタークロップ"
                  active={crop === "center"}
                  onClick={() => setCrop("center")}
                />
                <CropChip
                  label="フレーム合わせ"
                  active={crop === "fit"}
                  onClick={() => setCrop("fit")}
                />
              </div>
            </div>
            <div className="mt-4">
              <span className="block text-sm text-omc-text-secondary">出力解像度</span>
              <div className="mt-2">
                <span className="inline-block rounded bg-omc-accent px-3 py-1 text-sm text-white">
                  1080×1920
                </span>
              </div>
            </div>
          </section>

          {/* 音声・出力 */}
          <section className="rounded-lg border border-omc-border bg-omc-surface p-4">
            <h2 className="text-lg font-semibold">音声・出力</h2>
            <ToggleRow
              label="音声ノーマライズ (LUFS -14)"
              description="ショート向けに音量平準化"
              checked={normalize}
              onToggle={() => setNormalize((v) => !v)}
            />
            <div className="mt-4">
              <span className="block text-sm text-omc-text-secondary">
                出力ファイル名 (自動生成)
              </span>
              <code className="mt-1 block rounded border border-omc-border bg-omc-bg p-2 font-mono text-sm">
                clip_rank{String(rank).padStart(2, "0")}_{(runId ?? "").slice(-8)}.mp4
              </code>
            </div>
            <ToggleRow
              className="mt-4"
              label="背景透過オーバーレイ"
              description="同じ動画を背景に薄く重ねる (不透明度 0.3)"
              checked={bgOverlay}
              onToggle={() => setBgOverlay((v) => !v)}
              // UiCropMode は center / fit のみで none を含まないため、UI 上は常に有効。
              // 旧版で disabled={crop === "none"} が defensively あったが UI からは到達不可
              // (Copilot review 2nd #8 反映で型を絞った)。CLI 側 (none + bg=on) は backend 422。
              disabled={false}
            />
          </section>

          {/* 状態 + アクション */}
          <section className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="text-omc-text-secondary">状態: </span>
              <span className="font-semibold">{statusBadge}</span>
              {clipState?.error_message && (
                <p className="mt-1 text-omc-semantic-danger">{clipState.error_message}</p>
              )}
              {inputError && <p className="mt-1 text-omc-semantic-danger">{inputError}</p>}
            </div>
            <div className="flex gap-2">
              {clipState?.status === "success" && (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="rounded border border-omc-accent px-4 py-2 text-omc-accent hover:bg-omc-accent/10"
                >
                  ダウンロード
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="rounded bg-omc-accent px-4 py-2 text-white hover:bg-omc-accent/80 disabled:opacity-50"
              >
                {submitting
                  ? "送信中…"
                  : clipState?.status === "running"
                    ? "生成中…"
                    : clipState
                      ? "再生成"
                      : "クリップを生成"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

function statusToLabel(status: ClipStatus): string {
  switch (status) {
    case "queued":
      return "待機中…"
    case "running":
      return "生成中…"
    case "success":
      return "完了"
    case "failed":
      return "失敗"
  }
}

function CropChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded bg-omc-accent px-3 py-1 text-sm text-white"
          : "rounded border border-omc-border px-3 py-1 text-sm text-omc-text-secondary hover:border-omc-accent"
      }
    >
      {label}
    </button>
  )
}

function ToggleRow({
  label,
  description,
  checked,
  onToggle,
  disabled = false,
  className = "",
}: {
  label: string
  description?: string
  checked: boolean
  onToggle: () => void
  disabled?: boolean
  className?: string
}) {
  return (
    <div className={`flex items-start justify-between gap-3 ${className}`}>
      <div>
        <span className="block text-sm font-medium text-omc-text-primary">{label}</span>
        {description && (
          <span className="block text-xs text-omc-text-secondary">{description}</span>
        )}
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={
          checked
            ? "h-6 w-10 rounded-full bg-omc-accent disabled:opacity-40 transition"
            : "h-6 w-10 rounded-full bg-omc-border disabled:opacity-40 transition"
        }
        aria-pressed={checked}
      >
        <span
          className={
            checked
              ? "block h-4 w-4 translate-x-5 rounded-full bg-white transition"
              : "block h-4 w-4 translate-x-1 rounded-full bg-white transition"
          }
        />
      </button>
    </div>
  )
}
